import { useSearchParams } from "react-router-dom";
import { Calculator, PhoneCall, FileCheck2 } from "lucide-react";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { getProduct } from "@/lib/products";
import { inrFull } from "@/lib/format";

const STEPS = [
  { icon: FileCheck2, title: "Send your enquiry", text: "Two minutes. Name, number, amount — that's the heavy lifting done." },
  { icon: PhoneCall, title: "Advisor calls you", text: "Usually within one working day. We understand the need, then shortlist lenders." },
  { icon: Calculator, title: "Compare real offers", text: "You see actual rates and terms from partner lenders before you commit to anything." },
];

export default function ApplyPage() {
  const [params] = useSearchParams();
  const productSlug = params.get("product");
  const product = productSlug ? getProduct(productSlug) : null;
  const amount = params.get("amount");
  const rate = params.get("rate");
  const tenure = params.get("tenure");
  const emi = params.get("emi");

  useSEO({
    title: "Apply for a Loan — Free Enquiry | Loan Salaah",
    description: "Send a two-minute loan enquiry. A Loan Salaah advisor compares offers from partner banks and NBFCs and calls you back.",
  });

  const prefill = {
    loan_type: product ? product.name : undefined,
    amount: amount || "",
    rate: rate || "",
    tenure: tenure || "",
    emi: emi || "",
  };

  return (
    <div data-testid="apply-page" className="pb-24 pt-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Apply now</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl">
            Two minutes now. The right loan soon after.
          </h1>
          {emi && (
            <div className="mt-6 inline-block rounded-2xl border border-sage/50 bg-white px-5 py-4" data-testid="apply-prefill-summary">
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] text-slate">From your calculation</p>
              <p className="mt-1 text-sm text-charcoal">
                <strong className="text-concordia">{product?.name}</strong> · {inrFull(Number(amount))} · {rate}% · {tenure} mo
              </p>
              <p className="mt-1 font-serif text-2xl font-semibold text-concordia">EMI {inrFull(Number(emi))}</p>
            </div>
          )}
          <ol className="mt-10 space-y-7">
            {STEPS.map((s, i) => (
              <li key={s.title} className="flex gap-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 text-brass">
                  <s.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-serif text-lg font-semibold text-concordia">
                    <span className="mr-2 font-mono text-xs text-brass">0{i + 1}</span>
                    {s.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-slate">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-8 rounded-2xl border border-sand bg-white px-5 py-4 text-xs leading-relaxed text-slate" data-testid="apply-direct-email-note">
            Prefer email? Send your application directly to{" "}
            <a href="mailto:info@loansalaah.com" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">
              info@loansalaah.com
            </a>{" "}
            — include your name, phone, product and amount.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-7">
          <EnquiryForm prefill={prefill} />
        </Reveal>
      </div>
    </div>
  );
}
